import type { GaisukeArenaLeaderboardEntry } from '../services/systems/gaisuke/gaisuke.service';
import type {
  GaisukeFxArmPublic,
  GaisukeFxPolicyPublic,
  GaisukeFxPublicStatus,
} from '../services/systems/gaisuke/gaisukeFx.service';
import { GAISUKE_FX_SCOREBOARD_TIEBREAK } from './gaisukeFxArmMeta';

/** FX 公開スコアボードと同じ同額タイブレーク */
export { GAISUKE_FX_SCOREBOARD_TIEBREAK };

/** 総資産降順。同額は参戦中を先、さらに融合→キャリー…のタイブレーク */
export function sortGaisukeFxArmsByEquity<
  T extends { key: string; equityJpy: number; canTrade?: boolean },
>(arms: readonly T[]): T[] {
  return [...arms].sort((a, b) => {
    if (b.equityJpy !== a.equityJpy) return b.equityJpy - a.equityJpy;
    const aOn = a.canTrade !== false;
    const bOn = b.canTrade !== false;
    if (aOn !== bOn) return aOn ? -1 : 1;
    return (GAISUKE_FX_SCOREBOARD_TIEBREAK[a.key] ?? 99) - (GAISUKE_FX_SCOREBOARD_TIEBREAK[b.key] ?? 99);
  });
}

export type GaisukeArenaScoreboardMainEntry = GaisukeArenaLeaderboardEntry & {
  kind: 'main';
  rank: number;
};

export type GaisukeArenaScoreboardFxEntry = {
  kind: 'fx';
  rank: number;
  /** 合成リスト用の安定キー（本編 armId と衝突しない） */
  armId: number;
  fxArmKey: string;
  name: string;
  /** 戦略の一行説明（カード副題） */
  model: string;
  equityJpy: number;
  cumulativePnlJpy: number;
  returnPct: number;
  latestPolicy: GaisukeFxPolicyPublic | null;
  position: GaisukeFxArmPublic['position'];
  /** FX 公開カードと同じ詳細 UI 用の生アーム */
  arm: GaisukeFxArmPublic;
  experimentStartDate: string | null;
};

export type GaisukeArenaScoreboardMergedEntry =
  | GaisukeArenaScoreboardMainEntry
  | GaisukeArenaScoreboardFxEntry;

type GaisukeArenaScoreboardUnrankedEntry =
  | Omit<GaisukeArenaScoreboardMainEntry, 'rank'>
  | Omit<GaisukeArenaScoreboardFxEntry, 'rank'>;

const FX_SYNTHETIC_ARM_ID = -900_001;

function sortFxPolicies(policies: GaisukeFxPolicyPublic[]): GaisukeFxPolicyPublic[] {
  return [...policies].sort((a, b) => {
    // 決定時刻が正。同時刻だけ idle（現状）を採用指値・シグナル・会議より後ろへ
    if (a.decidedAt !== b.decidedAt) {
      return (b.decidedAt ?? '').localeCompare(a.decidedAt ?? '');
    }
    const aIdle = a.slot === 'idle' ? 1 : 0;
    const bIdle = b.slot === 'idle' ? 1 : 0;
    if (aIdle !== bIdle) return aIdle - bIdle;
    if (a.policyDate !== b.policyDate) return b.policyDate.localeCompare(a.policyDate);
    return b.slot.localeCompare(a.slot);
  });
}

/** FX 方針履歴の並び（公開カード・本編並立で共用） */
export function sortGaisukeFxPoliciesForDisplay(
  policies: GaisukeFxPolicyPublic[]
): GaisukeFxPolicyPublic[] {
  return sortFxPolicies(policies);
}

/** JST の今日 YYYY-MM-DD（指値期限表示のフォールバック用） */
export function gaisukeFxTodayYmdJst(now = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

/**
 * 指値の監視期限ラベル用。
 * am → 決定日 20:00。pm → executionDate 08:00。
 * 採用指値は limitCutoff（なければ executionDate と今日の比較）で昼/朝を決める。
 */
export function resolveGaisukeFxLimitDeadline(params: {
  slot: string;
  policyDate: string;
  executionDate?: string | null;
  limitCutoff?: 'evening' | 'morning' | null;
  todayYmd?: string;
}): { ymd: string; time: '20:00' | '08:00' } {
  const policyDate = params.policyDate || '';
  const execDate = params.executionDate || params.policyDate || '';
  const today = params.todayYmd ?? gaisukeFxTodayYmdJst();
  const isAdopt =
    params.slot === 'adopt' || params.slot === 'adopt2' || params.slot === 'adopt3';

  let evening: boolean;
  if (params.limitCutoff === 'evening') evening = true;
  else if (params.limitCutoff === 'morning') evening = false;
  else if (params.slot === 'am') evening = true;
  else if (params.slot === 'pm') evening = false;
  else if (isAdopt) {
    // 日またぎ再利用で policyDate が古いまま残りうる。executionDate が今日以前なら昼枠
    evening = !execDate || execDate <= today;
  } else {
    evening = !!policyDate && execDate === policyDate;
  }

  if (evening) {
    if (isAdopt) {
      // 日またぎ後は executionDate（今日）を期限日にする。古すぎる行は今日 20:00
      const ymd = execDate && execDate >= today ? execDate : today || policyDate || execDate;
      return { ymd, time: '20:00' };
    }
    return { ymd: policyDate || execDate, time: '20:00' };
  }
  return { ymd: execDate || policyDate, time: '08:00' };
}

/** 現金・参戦前を除き総資産最大の戦略。同額は融合→キャリー→タートルズ… */
export function pickGaisukeFxScoreboardLeadArm(
  arms: GaisukeFxArmPublic[] | undefined | null
): GaisukeFxArmPublic | null {
  const ranked = sortGaisukeFxArmsByEquity(
    (arms ?? []).filter((a) => a.key !== 'cash' && a.canTrade !== false)
  );
  return ranked[0] ?? null;
}

export function shouldIncludeFxOnMainScoreboard(
  status: GaisukeFxPublicStatus | undefined | null
): boolean {
  if (!status) return false;
  if (status.status === 'not_started') return false;
  return pickGaisukeFxScoreboardLeadArm(status.arms) != null;
}

export function toGaisukeArenaScoreboardFxEntry(
  arm: GaisukeFxArmPublic,
  labels: { name: string; model: string },
  experimentStartDate: string | null = null
): Omit<GaisukeArenaScoreboardFxEntry, 'rank'> {
  const latest = sortFxPolicies(arm.recentPolicies)[0] ?? null;
  return {
    kind: 'fx',
    armId: FX_SYNTHETIC_ARM_ID,
    fxArmKey: arm.key,
    name: labels.name,
    model: labels.model,
    equityJpy: arm.equityJpy,
    cumulativePnlJpy: arm.cumulativePnlJpy,
    returnPct: arm.pnlPct,
    latestPolicy: latest,
    position: arm.position,
    arm,
    experimentStartDate,
  };
}

/**
 * 本編 AI3 + FX 最優秀を損益率（%）降順で混ぜる。
 * 同率は本編を先、FX を後。
 */
export function mergeGaisukeArenaScoreboardWithFxLead(params: {
  mainEntries: GaisukeArenaLeaderboardEntry[];
  fxStatus: GaisukeFxPublicStatus | undefined | null;
  fxLabels: { name: string; model: string } | null;
}): GaisukeArenaScoreboardMergedEntry[] {
  const mainOnly: Omit<GaisukeArenaScoreboardMainEntry, 'rank'>[] = params.mainEntries
    .filter((e) => e.provider !== 'benchmark')
    .map((e) => ({ ...e, kind: 'main' as const }));

  const rows: GaisukeArenaScoreboardUnrankedEntry[] = [...mainOnly];

  if (shouldIncludeFxOnMainScoreboard(params.fxStatus) && params.fxLabels) {
    const lead = pickGaisukeFxScoreboardLeadArm(params.fxStatus!.arms);
    if (lead) {
      rows.push(
        toGaisukeArenaScoreboardFxEntry(
          lead,
          params.fxLabels,
          params.fxStatus!.startDate ?? null
        )
      );
    }
  }

  rows.sort((a, b) => {
    if (b.returnPct !== a.returnPct) return b.returnPct - a.returnPct;
    if (a.kind !== b.kind) return a.kind === 'main' ? -1 : 1;
    return 0;
  });

  return rows.map((row, i): GaisukeArenaScoreboardMergedEntry => {
    if (row.kind === 'fx') {
      return { ...row, rank: i + 1 };
    }
    return { ...row, rank: i + 1 };
  });
}
